import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Slider, ToggleGroup, ToggleGroupItem } from '@ibsys/kit';
import { ThresholdSlider } from '@/shared/ui';
import type { InfluenceFilter, TransitionSort } from './lib';

/** Опции ограничения числа отображаемых переходов (топ-N). `0` — показывать все. */
export const TOP_N_OPTIONS = [10, 20, 50, 0] as const;

type Props = {
  /** Порог надёжности (`minDialogs`), уходит в тело запроса */
  minDialogs: number;
  onMinDialogsChange: (value: number) => void;
  /** Режим сортировки списков и лент */
  sort: TransitionSort;
  onSortChange: (value: TransitionSort) => void;
  /** Ограничение топ-N (0 — все) */
  topN: number;
  onTopNChange: (value: number) => void;
  /** Фильтр по знаку влияния (все / повышают / понижают) */
  influence: InfluenceFilter;
  onInfluenceChange: (value: InfluenceFilter) => void;
  /** Доступные пороги близости (шаги ползунков) */
  thresholds: number[];
  /** Текущий порог по возражениям (источники, from) */
  objectionThreshold: number | undefined;
  /** Смена порога по возражениям */
  onObjectionThresholdChange: (value: number) => void;
  /** Текущий порог по аргументам (приёмники, to) */
  argumentThreshold: number | undefined;
  /** Смена порога по аргументам */
  onArgumentThresholdChange: (value: number) => void;
};

/**
 * Компактная панель контролов раздела «Переходы»: слайдер порога надёжности
 * (minDialogs), фильтр влияния, переключатель сортировки и ограничение топ-N.
 */
export const TransitionsControls: FC<Props> = ({
  minDialogs,
  onMinDialogsChange,
  sort,
  onSortChange,
  topN,
  onTopNChange,
  influence,
  onInfluenceChange,
  thresholds,
  objectionThreshold,
  onObjectionThresholdChange,
  argumentThreshold,
  onArgumentThresholdChange,
}) => {
  const { t } = useTranslation('strategist');

  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end lg:gap-8">
        <div className="flex flex-col gap-2 lg:w-72">
          <span className="text-xs text-muted-foreground">
            {t('transitions.controls.minDialogs', { value: minDialogs })}
          </span>
          <Slider
            value={[minDialogs]}
            onValueChange={(v: number[]) => onMinDialogsChange(v[0] ?? 1)}
            min={1}
            max={10}
            step={1}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">{t('transitions.controls.influence')}</span>
          <ToggleGroup value={influence} onValueChange={(v: string) => v && onInfluenceChange(v as InfluenceFilter)}>
            <ToggleGroupItem value="all" className="!flex-none whitespace-nowrap px-4">
              {t('transitions.controls.influenceAll')}
            </ToggleGroupItem>
            <ToggleGroupItem value="up" className="!flex-none whitespace-nowrap px-4">
              {t('transitions.controls.influenceUp')}
            </ToggleGroupItem>
            <ToggleGroupItem value="down" className="!flex-none whitespace-nowrap px-4">
              {t('transitions.controls.influenceDown')}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">{t('transitions.controls.sort')}</span>
          <ToggleGroup value={sort} onValueChange={(v: string) => v && onSortChange(v as TransitionSort)}>
            <ToggleGroupItem value="lift" className="!flex-none whitespace-nowrap px-4">
              {t('transitions.controls.byLift')}
            </ToggleGroupItem>
            <ToggleGroupItem value="n" className="!flex-none whitespace-nowrap px-4">
              {t('transitions.controls.byN')}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">{t('transitions.controls.topN')}</span>
          <ToggleGroup
            value={String(topN)}
            onValueChange={(v: string) => v != null && v !== '' && onTopNChange(Number(v))}
          >
            {TOP_N_OPTIONS.map((option) => (
              <ToggleGroupItem key={option} value={String(option)} className="!flex-none whitespace-nowrap px-4">
                {option === 0 ? t('transitions.controls.allN') : option}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <ThresholdSlider
          label={t('transitions.controls.thresholdObjection')}
          thresholds={thresholds}
          value={objectionThreshold}
          onChange={onObjectionThresholdChange}
          coarserLabel={t('threshold.coarser')}
          finerLabel={t('threshold.finer')}
        />
        <ThresholdSlider
          label={t('transitions.controls.thresholdArgument')}
          thresholds={thresholds}
          value={argumentThreshold}
          onChange={onArgumentThresholdChange}
          coarserLabel={t('threshold.coarser')}
          finerLabel={t('threshold.finer')}
        />
      </CardContent>
    </Card>
  );
};
